import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerPortalAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { 
  Upload, MessageSquare, FileImage, Star, Clock, CheckCircle, 
  ArrowRight, Sparkles, TrendingUp, Award, AlertCircle, Phone, Shield,
  TicketIcon, Calendar, LifeBuoy, Bell, Smartphone, Download
} from 'lucide-react';
import { InstallAppButton } from '../components/InstallAppButton';

const FREEZE_DRYER = 'https://customer-assets.emergentagent.com/job_66080daa-dc30-4cf8-b53e-8e5d6f014e3b/artifacts/ldxk5a9u_HomeLyph_7_Tray_No_Pump.png';

const PORTAL_TILES = [
  { label: 'Support Tickets', desc: 'Get help & track requests', icon: TicketIcon, path: '/my-portal/support', testid: 'tile-support' },
  { label: 'Warranty Claims', desc: 'Register & file claims', icon: Shield, path: '/my-portal/warranties', testid: 'tile-warranties' },
  { label: 'Book a Meeting', desc: 'Zoom or phone with a specialist', icon: Calendar, path: '/my-portal/book-meeting', testid: 'tile-book' },
  { label: 'My Uploads', desc: 'Share photos & videos', icon: Upload, path: '/my-portal/files', testid: 'tile-files' },
  { label: 'Testimonials', desc: 'Share your experience', icon: MessageSquare, path: '/my-portal/testimonials', testid: 'tile-testimonials' },
  { label: 'Resources', desc: 'Guides, FAQs & videos', icon: LifeBuoy, path: '/my-portal/resources', testid: 'tile-resources' },
];
import { format } from 'date-fns';

/**
 * CustomerPortalHomePage - Landing page for customer portal
 * Shows overview of uploads and testimonials with quick actions
 */
const CustomerPortalHomePage = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [officeStatus, setOfficeStatus] = useState({ isOpen: false, nextOpen: null, message: '' });

  useEffect(() => {
    fetchData();
    checkOfficeHours();
    // Re-check office hours every minute
    const interval = setInterval(checkOfficeHours, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkOfficeHours = () => {
    // Office hours: Monday-Friday, 9 AM - 4 PM Mountain Time
    const now = new Date();
    const options = { timeZone: 'America/Denver', hour: 'numeric', hour12: false, weekday: 'long' };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);
    let hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    if (hour === 24) hour = 0; // midnight edge case
    const dayOfWeek = parts.find(p => p.type === 'weekday')?.value;
    
    const isWeekday = !['Saturday', 'Sunday'].includes(dayOfWeek);
    const isBusinessHours = hour >= 9 && hour < 16; // 9 AM to 4 PM
    const isOpen = isWeekday && isBusinessHours;
    
    let message = '';
    let nextOpen = null;
    
    if (!isOpen) {
      if (!isWeekday) {
        message = 'Technical support is closed for the weekend. We will be back Monday at 9:00 AM MST.';
        nextOpen = 'Monday 9:00 AM';
      } else if (hour < 9) {
        message = 'Technical support opens today at 9:00 AM MST.';
        nextOpen = 'Today 9:00 AM';
      } else {
        message = 'Technical support is closed for the day. We will be back tomorrow at 9:00 AM MST.';
        nextOpen = 'Tomorrow 9:00 AM';
      }
    }
    
    setOfficeStatus({ isOpen, nextOpen, message });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [filesRes, testimonialsRes] = await Promise.all([
        customerPortalAPI.getMyFiles(),
        customerPortalAPI.getMyTestimonials()
      ]);
      setFiles(filesRes.data.files || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'reviewed': return 'bg-blue-100 text-blue-700';
      case 'resolved': return 'bg-emerald-100 text-emerald-700';
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const pendingFiles = files.filter(f => f.status === 'pending').length;
  const pendingTestimonials = testimonials.filter(t => t.status === 'pending').length;
  const approvedTestimonials = testimonials.filter(t => t.status === 'approved').length;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="customer-portal-home">
      {/* Office Hours Banner */}
      {!officeStatus.isOpen && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800 font-semibold flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Technical Support Office Hours
          </AlertTitle>
          <AlertDescription className="text-amber-700">
            <p>{officeStatus.message}</p>
            <p className="mt-2 text-sm">
              <strong>Office Hours:</strong> Monday - Friday, 9:00 AM - 4:00 PM (Mountain Standard Time)
            </p>
            <p className="mt-1 text-sm">
              You can still submit support tickets 24/7 and we&apos;ll respond during business hours.
            </p>
          </AlertDescription>
        </Alert>
      )}
      
      {officeStatus.isOpen && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <AlertTitle className="text-emerald-800 font-semibold">
            Technical Support is Open
          </AlertTitle>
          <AlertDescription className="text-emerald-700">
            Our team is available to assist you. Office hours: Mon-Fri, 9 AM - 4 PM MST
          </AlertDescription>
        </Alert>
      )}

      {/* Welcome Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#00486D] bg-gradient-to-br from-[#002036] to-[#001019]">
        <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-72 h-72 bg-[#00C0C0]/15 rounded-full blur-3xl" />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 p-6 sm:p-8 items-center">
          <div className="md:col-span-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C0C0] mb-2">Chinook Manufacturing Support</p>
            <h1 className="text-3xl sm:text-4xl font-semibold text-white font-['Outfit'] tracking-tight mb-2">
              Welcome back, {user?.first_name || 'Customer'}!
            </h1>
            <p className="text-[#94A3B8] max-w-lg">
              Register your equipment, get support, and manage everything for your HomeLyph &amp; D-Freeze freeze dryers in one place.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/my-portal/warranties">
                <Button className="bg-[#00C0C0] text-[#001828] font-semibold hover:bg-[#00D0E0] rounded-full" data-testid="home-register-product-btn">
                  <Shield className="h-4 w-4 mr-2" /> Register Your Product <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <InstallAppButton variant="outline" className="border-[#00C0C0] text-[#00C0C0] hover:bg-[#00C0C0]/10 rounded-full" />
            </div>
          </div>
          <div className="hidden md:flex justify-center">
            <img src={FREEZE_DRYER} alt="Chinook freeze dryer" className="max-h-52 w-auto drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* Quick Tiles (bento) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="portal-tiles">
        {PORTAL_TILES.map(({ label, desc, icon: Icon, path, testid }) => (
          <Link key={path} to={path} data-testid={testid}
            className="group bg-[#002036] border border-[#00486D] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#00C0C0] transition-all duration-300">
            <div className="inline-flex p-3 rounded-xl bg-[#00C0C0]/10 mb-3">
              <Icon className="h-6 w-6 text-[#00C0C0]" />
            </div>
            <h3 className="font-semibold text-white">{label}</h3>
            <p className="text-sm text-[#94A3B8] mt-0.5">{desc}</p>
          </Link>
        ))}
      </div>

      {/* PWA Install section */}
      <div className="relative overflow-hidden rounded-2xl border border-[#00C0C0]/30 bg-[#00C0C0]/10 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-[#00C0C0]/20">
              <Smartphone className="h-7 w-7 text-[#00D0E0]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white font-['Outfit']">Get the Chinook app on your phone</h3>
              <p className="text-sm text-[#94A3B8]">Install the portal as an app for one-tap access to support, chat and your warranties.</p>
            </div>
          </div>
          <InstallAppButton variant="default" className="bg-[#00C0C0] text-[#001828] font-semibold hover:bg-[#00D0E0] rounded-full" />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileImage className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Uploads</p>
                <p className="text-2xl font-bold">{files.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{pendingFiles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Testimonials</p>
                <p className="text-2xl font-bold">{testimonials.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Award className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Published</p>
                <p className="text-2xl font-bold">{approvedTestimonials}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Uploads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Uploads</CardTitle>
              <CardDescription>Your latest file submissions</CardDescription>
            </div>
            <Link to="/my-portal/files">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : files.length > 0 ? (
              <div className="space-y-3">
                {files.slice(0, 3).map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileImage className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">{file.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(file.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(file.status)}>{file.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileImage className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No uploads yet</p>
                <Link to="/my-portal/files">
                  <Button variant="link" size="sm">Upload your first file</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Testimonials */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">My Testimonials</CardTitle>
              <CardDescription>Your submitted reviews</CardDescription>
            </div>
            <Link to="/my-portal/testimonials">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              </div>
            ) : testimonials.length > 0 ? (
              <div className="space-y-3">
                {testimonials.slice(0, 3).map((testimonial) => (
                  <div key={testimonial.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`}
                          />
                        ))}
                      </div>
                      <Badge className={getStatusColor(testimonial.status)}>{testimonial.status}</Badge>
                    </div>
                    <p className="font-medium text-sm">{testimonial.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{testimonial.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Star className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No testimonials yet</p>
                <Link to="/my-portal/testimonials">
                  <Button variant="link" size="sm">Share your experience</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerPortalHomePage;
